import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, RotateCw, AlertTriangle, Pill, FlaskConical, Activity, ExternalLink } from 'lucide-react';
import apiClient from '../../../api/client';
import StructureImage from '../StructureImage';
import { buildHubUrl } from '../../../config/externalUrls';

// ── Types ────────────────────────────────────────────────────────────────────

interface MetaboliteResult {
  transformation: string;
  smiles: string;
  formula: string;
  exact_mass: number;
  delta_mass: number;
  rank: number;
}

interface MetabolismPrediction {
  parent: {
    smiles: string;
    formula: string;
    exact_mass: number;
  };
  metabolites: MetaboliteResult[];
  reaction_rules_loaded: number;
}

interface CypIsoform {
  inhibition_probability: number;
  risk: string;
  classification: 'green' | 'amber' | 'red';
  confidence: number;
}

interface AdmetMetabolism {
  cyp_inhibition: Record<string, CypIsoform>;
  metabolic_stability: {
    value: number;
    unit: string;
    classification: string;
    confidence: number;
    note: string;
  };
}

interface MetabolismTabProps {
  smiles: string;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const DOT_COLORS: Record<string, string> = {
  green: '#10b981',
  amber: '#f59e0b',
  red: '#ef4444',
};

const TrafficDot: React.FC<{ classification: string }> = ({ classification }) => (
  <span
    className="inline-block w-2 h-2 rounded-full flex-shrink-0"
    style={{ backgroundColor: DOT_COLORS[classification] || '#9ca3af' }}
  />
);

const RiskPill: React.FC<{ risk: string; classification: string }> = ({ risk, classification }) => (
  <span
    className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium"
    style={{
      backgroundColor: `${DOT_COLORS[classification] || '#9ca3af'}18`,
      color: DOT_COLORS[classification] || '#9ca3af',
    }}
  >
    {risk}
  </span>
);

const TRANSFORMATION_COLORS: Record<string, string> = {
  'Aliphatic hydroxylation': '#3b82f6',
  'Aromatic hydroxylation': '#8b5cf6',
  'N-demethylation': '#ec4899',
  'O-demethylation': '#f97316',
  'N-oxidation': '#ef4444',
  'S-oxidation': '#f59e0b',
  'Dealkylation': '#10b981',
  'Glucuronidation': '#06b6d4',
  'Epoxidation': '#6366f1',
};

// ── Main Component ───────────────────────────────────────────────────────────

const MetabolismTab: React.FC<MetabolismTabProps> = ({ smiles }) => {
  const [cypData, setCypData] = useState<AdmetMetabolism | null>(null);
  const [metabolites, setMetabolites] = useState<MetabolismPrediction | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetched, setFetched] = useState(false);
  const [metabolismHubAvailable, setMetabolismHubAvailable] = useState<boolean | null>(null);

  // Check metabolism hub health
  useEffect(() => {
    apiClient.post('/v2/admet/predict', { smiles: 'C' }, { timeout: 3000 })
      .then(() => setMetabolismHubAvailable(true))
      .catch((err: any) => {
        // Any HTTP response (even 4xx like 401) means the hub is reachable
        const status = err?.response?.status;
        setMetabolismHubAvailable(status != null && status < 500);
      });
  }, []);

  const fetchData = useCallback(async () => {
    if (!smiles) return;
    setLoading(true);
    setError(null);

    try {
      // Always fetch CYP data from ADMET hub (15s timeout to avoid hanging)
      const admetPromise = apiClient.post('/v2/admet/predict', { smiles }, { timeout: 15000 });

      // Try metabolism hub for metabolite predictions
      const metabolismPromise = metabolismHubAvailable !== false
        ? apiClient.post('/v2/metabolism/predict', { smiles }, { timeout: 15000 }).catch(() => null)
        : Promise.resolve(null);

      const [admetRes, metabRes] = await Promise.all([admetPromise, metabolismPromise]);

      if (admetRes.data?.metabolism) {
        setCypData(admetRes.data.metabolism);
      }

      if (metabRes?.data) {
        setMetabolites(metabRes.data);
      }

      setFetched(true);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 502 || status === 503) {
        setError('Metabolism prediction is currently unavailable. The service may be restarting — try again in a few minutes.');
      } else if (err.response) {
        setError(err.response.data?.detail || err.message || 'Metabolism prediction failed');
      } else if (err.code === 'ECONNABORTED') {
        setError('Metabolism prediction timed out. The service may be overloaded — try again in a moment.');
      } else {
        setError('Could not reach the metabolism services');
      }
      setFetched(true);
    } finally {
      setLoading(false);
    }
  }, [smiles, metabolismHubAvailable]);

  // Auto-fetch
  useEffect(() => {
    if (!fetched && smiles) {
      fetchData();
    }
  }, [smiles, fetched, fetchData]);

  // Reset on SMILES change
  useEffect(() => {
    setFetched(false);
    setCypData(null);
    setMetabolites(null);
    setError(null);
  }, [smiles]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#0d9488' }} />
        <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Analysing metabolic liabilities...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 rounded-lg" style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a' }}>
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle size={14} style={{ color: '#d97706' }} />
          <span className="text-sm font-medium" style={{ color: '#92400e' }}>Could not reach the metabolism services</span>
        </div>
        <p className="text-xs mb-3" style={{ color: '#a16207' }}>The hubs may be restarting. Try again in a moment.</p>
        <button
          onClick={fetchData}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors"
          style={{ backgroundColor: '#0d9488', color: 'white' }}
        >
          <RotateCw size={12} /> Retry
        </button>
      </div>
    );
  }

  if (!fetched) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <FlaskConical size={24} style={{ color: 'var(--text-muted)' }} />
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Metabolism Analysis</p>
        <p className="text-xs text-center max-w-xs" style={{ color: 'var(--text-muted)' }}>
          Predict CYP interactions, metabolic stability, and likely metabolites.
        </p>
        <button
          onClick={fetchData}
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-colors"
          style={{ backgroundColor: '#0d9488', color: 'white' }}
        >
          <Activity size={13} /> Analyse
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button
          onClick={() => window.open(buildHubUrl('metabolism', { smiles }), '_blank')}
          title="Open Metabolism Hub in a new tab with this compound"
          className="flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-colors"
          style={{ color: '#0d9488', border: '1px solid rgba(13,148,136,0.3)', backgroundColor: 'transparent' }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(13,148,136,0.08)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
        >
          <ExternalLink size={11} />
          Open in Metabolism Hub
        </button>
      </div>
      {/* CYP Interaction Profile */}
      {cypData && (
        <div className="rounded-lg border overflow-hidden" style={{ borderColor: 'var(--card-border)', backgroundColor: 'var(--card-bg)' }}>
          <div
            className="flex items-center gap-2 px-3 py-2 border-b"
            style={{ borderColor: 'var(--card-border)', backgroundColor: 'var(--hover-bg)' }}
          >
            <Activity size={13} style={{ color: '#8b5cf6' }} />
            <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>
              CYP Interaction Profile
            </span>
          </div>

          {/* CYP table */}
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr style={{ backgroundColor: 'var(--hover-bg)' }}>
                  <th className="px-3 py-1.5 text-left font-semibold" style={{ color: 'var(--text-secondary)' }}>Isoform</th>
                  <th className="px-3 py-1.5 text-center font-semibold" style={{ color: 'var(--text-secondary)' }}>Inhibitor?</th>
                  <th className="px-3 py-1.5 text-right font-semibold" style={{ color: 'var(--text-secondary)' }}>Probability</th>
                  <th className="px-3 py-1.5 text-right font-semibold" style={{ color: 'var(--text-secondary)' }}>Risk</th>
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: 'var(--card-border)' }}>
                {Object.entries(cypData.cyp_inhibition).map(([isoform, data]) => (
                  <tr key={isoform}>
                    <td className="px-3 py-1.5 font-mono font-medium" style={{ color: 'var(--text-primary)' }}>
                      <div className="flex items-center gap-2">
                        <TrafficDot classification={data.classification} />
                        {isoform}
                      </div>
                    </td>
                    <td className="px-3 py-1.5 text-center">
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold"
                        style={{
                          backgroundColor: data.classification === 'green' ? '#10b98118' : data.classification === 'amber' ? '#f59e0b18' : '#ef444418',
                          color: DOT_COLORS[data.classification],
                          border: `1px solid ${DOT_COLORS[data.classification]}30`,
                        }}
                      >
                        {data.inhibition_probability > 0.5 ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono" style={{ color: 'var(--text-primary)' }}>
                      {(data.inhibition_probability * 100).toFixed(0)}%
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <RiskPill risk={data.risk} classification={data.classification} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Metabolic stability */}
          <div className="px-3 py-2 border-t flex items-center justify-between" style={{ borderColor: 'var(--card-border)' }}>
            <div className="flex items-center gap-2">
              <TrafficDot classification={cypData.metabolic_stability.classification} />
              <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Metabolic Stability</span>
            </div>
            <span className="text-xs font-mono font-medium" style={{ color: 'var(--text-primary)' }}>
              {typeof cypData.metabolic_stability.value === 'number'
                ? cypData.metabolic_stability.value.toFixed(1)
                : cypData.metabolic_stability.value}
              {cypData.metabolic_stability.unit && (
                <span className="text-[10px] ml-1" style={{ color: 'var(--text-muted)' }}>
                  {cypData.metabolic_stability.unit}
                </span>
              )}
            </span>
          </div>
        </div>
      )}

      {/* Predicted Metabolites */}
      {metabolites && metabolites.metabolites.length > 0 && (
        <div className="rounded-lg border overflow-hidden" style={{ borderColor: 'var(--card-border)', backgroundColor: 'var(--card-bg)' }}>
          <div
            className="flex items-center gap-2 px-3 py-2 border-b"
            style={{ borderColor: 'var(--card-border)', backgroundColor: 'var(--hover-bg)' }}
          >
            <FlaskConical size={13} style={{ color: '#0d9488' }} />
            <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>
              Predicted Metabolites ({metabolites.metabolites.length})
            </span>
          </div>
          <div className="divide-y" style={{ borderColor: 'var(--card-border)' }}>
            {metabolites.metabolites.map((m, i) => {
              const color = TRANSFORMATION_COLORS[m.transformation] || '#6b7280';
              return (
                <div key={i} className="px-3 py-2.5">
                  <div className="flex items-center justify-between mb-1">
                    <span
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium"
                      style={{ backgroundColor: `${color}18`, color, border: `1px solid ${color}30` }}
                    >
                      {m.transformation}
                    </span>
                    <span className="text-[10px] font-mono" style={{ color: 'var(--text-muted)' }}>
                      Rank #{m.rank}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-1.5">
                    <StructureImage smiles={m.smiles} size={60} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mt-0.5">
                        <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                          {m.formula}
                        </span>
                        <span className="text-[10px] font-mono" style={{ color: 'var(--text-muted)' }}>
                          MW {m.exact_mass.toFixed(2)}
                        </span>
                        <span className="text-[10px] font-mono" style={{ color: '#0d9488' }}>
                          +{m.delta_mass.toFixed(3)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {metabolismHubAvailable === false && (
        <div className="text-center py-4 text-xs" style={{ color: 'var(--text-muted)' }}>
          Metabolite prediction service is not available. CYP data shown from ADMET hub.
        </div>
      )}

      {metabolites && metabolites.metabolites.length === 0 && (
        <div className="text-center py-4 text-xs" style={{ color: 'var(--text-muted)' }}>
          No metabolites predicted for this compound.
        </div>
      )}
    </div>
  );
};

export default MetabolismTab;
